import { useCallback, useEffect, useRef, useState } from 'react';

const Flatpicker = ({
  className,
  options = {},
  placeholder,
  value,
  getValue
}) => {
  const element = useRef(null);
  const wrapperRef = useRef(null);
  const instanceRef = useRef(null);
  const isRangeMode = options?.mode === 'range';
  const optionsRef = useRef(options);
  const isClosingRef = useRef(false);
  const shouldPreventCloseRef = useRef(false);
  const [formattedValue, setFormattedValue] = useState('');
  const formattedValueRef = useRef('');
  const lastRangeValueRef = useRef(null);
  /** Always call latest parent callback without changing identity — avoids recreating Flatpickr every render. */
  const getValueRef = useRef(getValue);
  getValueRef.current = getValue;
  const valueRef = useRef(value);
  valueRef.current = value;

  // Keep options ref updated without causing recreation
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);
  
  // Format date range for display
  const formatDateRange = useCallback((selectedDates, dateFormat, instance) => {
    if (!selectedDates || selectedDates.length === 0) return '';
    
    if (isRangeMode && selectedDates.length === 2) {
      const formatDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        if (isNaN(d.getTime())) return '';
        
        // Use Flatpickr's formatDate if available
        if (instance && instance.formatDate) {
          return instance.formatDate(d, dateFormat || 'd M y');
        }
        
        // Fallback formatting based on common formats
        const dayOfWeek = d.toLocaleString('en-US', { weekday: 'short' }); // 3-letter day (Mon, Tue, etc.)
        const day = d.getDate();
        const month = d.toLocaleString('en-US', { month: 'short' }); // 3-letter month (Jan, Feb, etc.)
        
        // Match the dateFormat pattern
        if (dateFormat) {
          return dateFormat
            .replace(/D/g, dayOfWeek) // Day of week (3 letters)
            .replace(/d/g, day) // Day of month
            .replace(/M/g, month) // Month (3 letters)
            .replace(/y/g, '') // Remove year
            .replace(/Y/g, ''); // Remove year
        }
        
        return `${dayOfWeek} ${day} ${month}`;
      };
      
      const fromDate = formatDate(selectedDates[0]);
      const toDate = formatDate(selectedDates[1]);
      
      if (fromDate && toDate) {
        return `${fromDate} <strong>-</strong> ${toDate}`;
      }
    }
    
    return '';
  }, [isRangeMode]);

  const handleDateChange = useCallback(selectedDates => {
    // For range mode: only update parent state when both dates are selected
    // This prevents re-renders that might close the calendar
    if (isRangeMode) {
      if (selectedDates.length === 2) {
        // Both dates selected - safe to update parent
        getValueRef.current?.(selectedDates);
      }
      // Don't update parent when only one date is selected
      // This prevents state updates that cause re-renders and calendar closing
    } else {
      // For non-range mode, update normally
      const newDate = selectedDates.length === 1 ? selectedDates[0] : selectedDates;
      getValueRef.current?.(newDate);
      setFormattedValue('');
    }
  }, [isRangeMode]);

  // Initialize Flatpickr instance (only once or when mode changes)
  useEffect(() => {
    formattedValueRef.current = formattedValue;
  }, [formattedValue]);

  useEffect(() => {
    if (!element.current) return;

    // Extract handlers from options to avoid recreation issues
    const { onChange: originalOnChange, onClose: originalOnClose, onReady: originalOnReady, ...restOptions } = optionsRef.current;

    const normalizedDateFormat = restOptions?.dateFormat || (restOptions?.noCalendar ? 'H:i' : 'D, d M');

    // Build final options - ensure closeOnSelect is false for range mode
    const finalOptions = {
      ...restOptions,
      dateFormat: normalizedDateFormat,
      appendTo:
        restOptions.appendTo ??
        (typeof document !== 'undefined' ? document.body : undefined),
      // CRITICAL: For range mode, must set closeOnSelect to false
      closeOnSelect: isRangeMode ? false : (restOptions.closeOnSelect !== undefined ? restOptions.closeOnSelect : true),
      // Prevent closing on outside click if range is incomplete
      clickOpens: true,
      onChange: (selectedDates, dateStr, instance) => {
        // Ensure selectedDates is an array
        if (!selectedDates || !Array.isArray(selectedDates)) {
          selectedDates = [];
        }
        
        // Call original onChange if provided
        if (originalOnChange) {
          originalOnChange(selectedDates, dateStr, instance);
        }
        
        // For range mode: set flag to prevent closing if only one date selected
        if (isRangeMode) {
          if (selectedDates.length === 1) {
            shouldPreventCloseRef.current = true;
            // Keep calendar open
            if (!instance.isOpen) {
              instance.open();
            }
          } else if (selectedDates.length === 2) {
            shouldPreventCloseRef.current = false;
            isClosingRef.current = true;
            // Both dates selected - close after a brief delay
            setTimeout(() => {
              if (instance && instance.isOpen) {
                instance.close();
              }
              isClosingRef.current = false;
            }, 150);
          }
        }
        
        // Handle date change
        handleDateChange(selectedDates);
        
        // Update formatted display for range mode
        if (isRangeMode && selectedDates.length === 2) {
          const formatted = formatDateRange(
            selectedDates,
            instance?.config?.dateFormat || normalizedDateFormat,
            instance,
          );
          setFormattedValue(formatted);
          // Clear input value to prevent double text
          if (element.current) {
            element.current.value = '';
          }
        } else if (isRangeMode && selectedDates.length === 1) {
          // Show partial range while selecting
          const formatted = formatDateRange(
            [selectedDates[0], selectedDates[0]],
            instance?.config?.dateFormat || normalizedDateFormat,
            instance,
          );
          setFormattedValue(formatted);
          // Clear input value to prevent double text
          if (element.current) {
            element.current.value = '';
          }
        } else if (isRangeMode) {
          setFormattedValue('');
        }
      },
      onClose: (selectedDates, dateStr, instance) => {
        // For range mode: aggressively prevent closing if only one date is selected
        if (isRangeMode && !isClosingRef.current) {
          const selectedCount = selectedDates ? selectedDates.length : 0;
          
          if (selectedCount === 1 || shouldPreventCloseRef.current) {
            // Prevent close - reopen immediately
            shouldPreventCloseRef.current = true;
            requestAnimationFrame(() => {
              if (instance && !instance.isOpen) {
                instance.open();
              }
            });
            return; // Don't call original onClose if we're preventing the close
          }
        }
        
        // Call original onClose if provided
        if (originalOnClose) {
          originalOnClose(selectedDates, dateStr, instance);
        }
      },
      onValueUpdate: (_selectedDates, _dateStr, _instance) => {
        // For range mode: clear input value to prevent double text
        // We use our custom overlay instead
        if (isRangeMode && element.current && formattedValueRef.current) {
          element.current.value = '';
        }
      },
      onReady: (selectedDates, dateStr, instance) => {
        // Call original onReady if provided
        if (originalOnReady) {
          originalOnReady(selectedDates, dateStr, instance);
        }
        
        // For range mode: enforce closeOnSelect is disabled at instance level
        if (isRangeMode && instance.config) {
          instance.config.closeOnSelect = false;
          
          // Override the close method to prevent premature closing
          const originalClose = instance.close.bind(instance);
          instance.close = function(preventOpen) {
            const selectedCount = this.selectedDates ? this.selectedDates.length : 0;
            
            // If in range mode and only one date selected, prevent closing
            if (isRangeMode && selectedCount === 1 && !isClosingRef.current && !preventOpen) {
              shouldPreventCloseRef.current = true;
              return; // Don't close
            }
            
            // Otherwise, allow normal close
            originalClose(preventOpen);
          };
          
          // Monitor for premature closes and reopen if needed
          const checkAndReopen = () => {
            if (instance.isOpen === false && shouldPreventCloseRef.current) {
              const selectedCount = instance.selectedDates ? instance.selectedDates.length : 0;
              if (selectedCount === 1) {
                setTimeout(() => {
                  if (!instance.isOpen) {
                    instance.open();
                  }
                }, 10);
              }
            }
          };
          
          // Check periodically if calendar was closed prematurely
          const intervalId = setInterval(checkAndReopen, 100);
          instance._rangeCheckInterval = intervalId;
          
          // Clear input value whenever Flatpickr updates it in range mode
          const originalSetDate = instance.setDate.bind(instance);
          instance.setDate = function(dates, triggerChange = true) {
            const result = originalSetDate(dates, triggerChange);
            // Clear input value after Flatpickr sets it
            if (isRangeMode && element.current && formattedValueRef.current) {
              setTimeout(() => {
                if (element.current) {
                  element.current.value = '';
                }
              }, 0);
            }
            return result;
          };
        }
      }
    };
    
    let cancelled = false;
    import('flatpickr').then((mod) => {
      if (cancelled || !element.current) return;
      const flatpickrFn = mod.default || mod;
      if (!instanceRef.current || (instanceRef.current.config?.mode !== isRangeMode)) {
        if (instanceRef.current && typeof instanceRef.current.destroy === 'function') {
          try {
            instanceRef.current.destroy();
          } catch {
            // Ignore destroy errors
          }
          instanceRef.current = null;
        }
        const initialValue = valueRef.current;
        instanceRef.current = flatpickrFn(element.current, {
          ...finalOptions,
          defaultDate: finalOptions.defaultDate ?? (initialValue || undefined),
        });
        if (initialValue && typeof instanceRef.current.setDate === 'function') {
          instanceRef.current.setDate(
            Array.isArray(initialValue) ? initialValue : [initialValue],
            false,
          );
        }
      }
    }).catch(() => {});
    
    return () => {
      cancelled = true;
      if (instanceRef.current) {
        // Clear interval if it exists
        if (instanceRef.current._rangeCheckInterval) {
          clearInterval(instanceRef.current._rangeCheckInterval);
        }
        if (typeof instanceRef.current.destroy === 'function') {
          try {
        instanceRef.current.destroy();
          } catch {
            // Ignore destroy errors
          }
        }
        instanceRef.current = null;
      }
    };
  }, [formatDateRange, handleDateChange, isRangeMode]);

  // Sync min/max/disable + day hooks when options change without destroying the instance
  useEffect(() => {
    const inst = instanceRef.current;
    if (!inst?.set) return;
    const current = optionsRef.current || {};
    const { minDate, maxDate, disable, onDayCreate, onMonthChange } = current;
    try {
      if (minDate !== undefined) inst.set('minDate', minDate);
      if (maxDate !== undefined) inst.set('maxDate', maxDate);
      if (disable !== undefined) inst.set('disable', disable);
      if (onDayCreate && inst.config) inst.config.onDayCreate = [onDayCreate];
      if (onMonthChange && inst.config) inst.config.onMonthChange = [onMonthChange];
      if (typeof inst.redraw === 'function') inst.redraw();
    } catch {
      // ignore
    }
  }, [options]);

  // Update instance value without recreating (prevents calendar from closing)
  useEffect(() => {
    const instance = instanceRef.current;
    if (!instance || value === undefined) return;

    // Non-range mode: keep existing behavior
    if (!isRangeMode) {
      const currentDates = instance.selectedDates || [];
      const valueArray = Array.isArray(value) ? value : (value ? [value] : []);
      
      const datesChanged = 
        currentDates.length !== valueArray.length ||
        currentDates.some((date, idx) => {
          const valDate = valueArray[idx];
          return !valDate || date.getTime() !== new Date(valDate).getTime();
        });
      
      if (datesChanged) {
        instance.setDate(valueArray, false);
      }
      return;
    }

    // Range mode: allow external updates when both dates provided
    if (!Array.isArray(value) || value.length !== 2) {
      lastRangeValueRef.current = null;
      if (!value || value.length === 0) {
        setFormattedValue('');
        instance.clear();
      }
      return;
    }

    const parsedDates = value
      .map((date) => (date instanceof Date ? date : new Date(date)))
      .filter((date) => date instanceof Date && !isNaN(date.getTime()));

    if (parsedDates.length !== 2) return;

    const serialized = parsedDates.map((date) => date.getTime()).join('|');
    if (serialized === lastRangeValueRef.current) return;
    lastRangeValueRef.current = serialized;

    instance.setDate(parsedDates, false);

    const formatted = formatDateRange(parsedDates, instance?.config?.dateFormat || optionsRef.current?.dateFormat, instance);
    setFormattedValue(formatted || '');
    if (element.current) {
      element.current.value = '';
    }
    shouldPreventCloseRef.current = false;
    isClosingRef.current = false;
  }, [value, isRangeMode, formatDateRange]);

  // Get input padding to match overlay and clear input value in range mode
  useEffect(() => {
    if (isRangeMode && element.current) {
      const input = element.current;
      
      if (formattedValue) {
        // Clear the input value to prevent double text
        // We'll use the overlay to display our formatted text
        if (input.value) {
          input.value = '';
        }
        
        const computedStyle = window.getComputedStyle(input);
        const overlay = wrapperRef.current?.querySelector('.flatpickr-range-overlay');
        if (overlay) {
          // Match the input's padding exactly to align with other form fields
          overlay.style.paddingLeft = computedStyle.paddingLeft;
          overlay.style.paddingRight = computedStyle.paddingRight;
          overlay.style.fontSize = computedStyle.fontSize;
          overlay.style.lineHeight = computedStyle.lineHeight;
          overlay.style.fontWeight = computedStyle.fontWeight;
          overlay.style.color = '#1a1a1a';
        }
      }
    }
  }, [formattedValue, isRangeMode]);

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
      <input 
        ref={element} 
        className={`form-control flatpickr ${className}`}
        autoComplete="off"
        readOnly
        placeholder={isRangeMode && formattedValue ? '' : placeholder}
        style={isRangeMode && formattedValue ? { 
          color: 'transparent',
          caretColor: 'transparent',
          textShadow: '0 0 0 transparent'
        } : {}}
      />
      {isRangeMode && formattedValue && (
        <div
          className="flatpickr-range-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '0.15rem',
            paddingRight: '0.75rem',
            pointerEvents: 'none',
            fontSize: 'inherit',
            lineHeight: 'inherit',
            color: '#1a1a1a',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            userSelect: 'none'
          }}
        >
          <span dangerouslySetInnerHTML={{
            __html: formattedValue
          }} />
        </div>
      )}
    </div>
  );
};

export default Flatpicker;